import { Avatar } from '@/components/ui/avatar';
import { CompanionCardHeader } from '@/components/companion/cards/CompanionCardHeader';
import { CompanionCardShell, EvidenceLine } from '@/components/companion/cards/CompanionCardShell';
import { formatCompanionMoney } from '@/components/companion/feed/companion-formatters';
import { HStack, Stack, Text } from '@chakra-ui/react';
import type { CompanionActionDto, CompanionAffiliateSilentEvidence } from '@fluenceur/types';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The silent-affiliate card, as the companion panel shows it.
 *
 * It states only what the detector froze: how long ago the last sale was, how
 * many orders proved the relationship, and what those orders were worth in the
 * currency they were placed in. Nothing is extrapolated — no "lost revenue", no
 * projection — because a brand is about to write to a person on the strength of
 * these lines, and every one of them has to be checkable.
 */

interface AffiliateSilentCardProps {
  action: CompanionActionDto & { evidence: CompanionAffiliateSilentEvidence };
  /** Opens the composer with this affiliate. Wired by the panel, never here. */
  onOpenCta: () => void;
  /** Starts the dismissal choice. Absent while the panel does not offer it. */
  onDismiss?: () => void;
  isCtaPending?: boolean;
  /** The workflow has no adapter in this build; the button is shown inert. */
  isCtaDisabled?: boolean;
  /** Fills the panel edge to edge: no frame of its own, the panel is the frame. */
  flat?: boolean;
  /** The host's own controls, rendered at the end of the action row. */
  footerEnd?: ReactNode;
}

export const AffiliateSilentCard = ({
  action,
  onOpenCta,
  onDismiss,
  isCtaPending = false,
  isCtaDisabled = false,
  flat = false,
  footerEnd,
}: AffiliateSilentCardProps) => {
  const { t, i18n } = useTranslation();
  const { evidence } = action;
  const name = evidence.displayName ?? t('companion.cards.affiliateSilent.unknownAffiliate');
  const revenue = evidence.observedRevenue
    ? formatCompanionMoney(evidence.observedRevenue, i18n.language)
    : null;

  return (
    <CompanionCardShell
      testId="companion-affiliate-silent-card"
      flat={flat}
      ctaLabel={action.ctaLabel}
      onOpenCta={onOpenCta}
      onDismiss={onDismiss}
      isCtaPending={isCtaPending}
      isCtaDisabled={isCtaDisabled}
      footerEnd={footerEnd}
    >
      <CompanionCardHeader title={action.title} reason={action.reason} />

      <HStack
        gap="10px"
        align="center"
        px="12px"
        py="10px"
        borderWidth="1px"
        borderColor="cardBorder"
        borderRadius="10px"
        bg="panelBg"
      >
        {/* The named size the dashboard gives every affiliate row (see
            AffiliateInfo): the initials scale with the circle, which a
            hand-set box does not. */}
        <Avatar name={name} size="xs" colorPalette="primary" />
        <Stack gap="2px" flex={1} minW={0}>
          <Text
            fontFamily="heading"
            fontSize="13px"
            fontWeight="600"
            color="ink"
            lineClamp={1}
            lineHeight="1.3"
          >
            {name}
          </Text>
          <EvidenceLine>
            {t('companion.cards.affiliateSilent.lastSale', { count: evidence.daysSilent })}
          </EvidenceLine>
          <EvidenceLine>
            {t('companion.cards.affiliateSilent.orders', { count: evidence.ordersInWindow })}
          </EvidenceLine>
          {revenue ? (
            <EvidenceLine>
              {t('companion.cards.affiliateSilent.revenue', { amount: revenue })}
            </EvidenceLine>
          ) : null}
        </Stack>
      </HStack>
    </CompanionCardShell>
  );
};
